import * as THREE from 'three'

import { ENV } from '../config/constants.js'

/**
 * The sea.
 *
 * Two pieces, because one mesh cannot do both jobs. A tessellated tile that
 * follows the camera provides wave geometry you can actually see from a
 * hundred feet on final; an enormous flat disc underneath provides horizon out
 * to the visibility limit without a million triangles.
 *
 * The tile snaps to a grid whose spacing matches its own quad size, and the
 * wave function is evaluated in absolute world coordinates. Without both of
 * those the crests slide under the aircraft as the camera moves and the whole
 * ocean appears to crawl — the classic infinite-terrain artefact.
 *
 * Geometry note: the plane is built in the XY plane and rotated -90 degrees
 * about X, which maps local +Z onto world +Y. That is why wave height is
 * written into the local Z component and not Y.
 */

/**
 * Defaults, overridden per device. The wave tile is the heaviest thing in the
 * frame and its cost is the SQUARE of the segment count — 240 to 96 is not a
 * 60% saving, it is an 84% one — so this is the first knob a phone turns.
 */
const DEFAULT_TILE_SIZE_M = 7000
const DEFAULT_TILE_SEGMENTS = 240

/**
 * The sum of the six per-component amplitude weights in `waveHeight` below.
 *
 * Kept here in JavaScript as well as in the GLSL because the horizon disc has
 * to sit BELOW the deepest possible trough, and the deepest possible trough is
 * `uAmplitude * WAVE_AMP_SUM` — the moment every component happens to line up
 * in phase. `scripts/test-shaders.mjs` re-adds the literals in the shader and
 * fails if the two ever drift apart.
 */
export const WAVE_AMP_SUM = 2.39

const vertexShader = /* glsl */ `
  uniform float uTime;
  uniform float uAmplitude;
  uniform vec2  uWindDir;
  uniform vec2  uOffset;

  varying vec3 vWorldPos;
  varying vec3 vWorldNormal;
  varying float vViewDist;
  varying float vTileDist;
  varying float vWaveHeight;
  varying float vSteepness;

  // Six directional waves rather than three.
  //
  // Three is not enough: with only a handful of components the crests line up
  // into a visible rectangular lattice that reads as corrugated iron, not sea.
  // The fix is more components with mutually irrational-ish directions and
  // wavelengths, so no two ever come back into phase across the tile. The
  // steepness also falls with wavelength, which is what makes short chop ride
  // on long swell instead of all of it looking like the same wave.
  float waveHeight(vec2 p, out vec2 grad) {
    const int N = 6;
    vec2 dirs[6];
    float lens[6];
    float amps[6];
    float spds[6];

    dirs[0] = normalize(vec2( 1.000,  0.230));  lens[0] = 97.0; amps[0] = 1.00; spds[0] = 0.55;
    dirs[1] = normalize(vec2(-0.517,  1.000));  lens[1] = 61.3; amps[1] = 0.62; spds[1] = 0.71;
    dirs[2] = normalize(vec2( 0.803, -0.741));  lens[2] = 33.7; amps[2] = 0.37; spds[2] = 0.98;
    dirs[3] = normalize(vec2(-0.911, -0.395));  lens[3] = 19.1; amps[3] = 0.21; spds[3] = 1.31;
    dirs[4] = normalize(vec2( 0.271,  0.962));  lens[4] = 11.3; amps[4] = 0.12; spds[4] = 1.77;
    dirs[5] = normalize(vec2( 0.643, -0.259));  lens[5] =  6.7; amps[5] = 0.07; spds[5] = 2.34;

    float h = 0.0;
    grad = vec2(0.0);

    for (int i = 0; i < N; i++) {
      float k = 6.2831853 / lens[i];
      float a = uAmplitude * amps[i];
      float ph = dot(dirs[i], p) * k + uTime * spds[i];
      h += a * sin(ph);
      grad += dirs[i] * (a * k * cos(ph));
    }
    return h;
  }

  void main() {
    // Local (x, y) maps to world (x, -y) once the mesh rotation is applied.
    vec2 worldXZ = vec2(position.x + uOffset.x, -position.y + uOffset.y);

    vec2 grad;
    float h = waveHeight(worldXZ, grad);
    vWaveHeight = h;
    vSteepness = length(grad);

    vWorldPos = vec3(worldXZ.x, h, worldXZ.y);
    vWorldNormal = normalize(vec3(-grad.x, 1.0, -grad.y));

    // Height goes into local Z, which the mesh rotation turns into world up.
    // Distance from the tile centre, used to fade the tile into the horizon
    // disc so the two never meet at a visible edge.
    vTileDist = length(vec2(position.x, position.y));

    vec4 mv = modelViewMatrix * vec4(position.x, position.y, h, 1.0);
    vViewDist = -mv.z;
    gl_Position = projectionMatrix * mv;
  }
`

const fragmentShader = /* glsl */ `
  uniform vec3  uSunDir;
  uniform vec3  uDeepColor;
  uniform vec3  uShallowColor;
  uniform vec3  uSkyColor;
  uniform vec3  uFogColor;
  uniform float uFogFar;

  uniform float uTileFadeStart;
  uniform float uTileFadeEnd;
  uniform float uFoam;
  // Also declared in the vertex stage. GLSL has no shared scope between the
  // two — a uniform used in the fragment shader and declared only in the
  // vertex shader is a compile error, and a compile error in a ShaderMaterial
  // is a black screen with a console message you have to go looking for.
  uniform float uTime;
  uniform float uAmplitude;
  uniform vec2  uWindDir;

  varying vec3 vWorldPos;
  varying vec3 vWorldNormal;
  varying float vViewDist;
  varying float vTileDist;
  varying float vWaveHeight;
  varying float vSteepness;

  // Cheap value noise, used to roughen the surface normal at a scale far finer
  // than the mesh can resolve. Without it the sea is glassy between crests and
  // the specular reads as plastic.
  float hash(vec2 p) {
    // Wrapped before hashing. The ship steams at thirty knots, so world
    // coordinates run into the tens of thousands within minutes, and
    // sin(2e6) on a float32 has an ULP of a quarter of a radian — the noise
    // collapses into flat bands and does it differently on every GPU.
    p = mod(p, 512.0);
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
  }
  float valueNoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash(i), hash(i + vec2(1,0)), u.x),
               mix(hash(i + vec2(0,1)), hash(i + vec2(1,1)), u.x), u.y);
  }

  // Linear -> sRGB. ShaderMaterial does not get three's automatic colour-space
  // conversion chunk, so it is done by hand here.
  vec3 toSRGB(vec3 c) {
    return mix(c * 12.92,
               1.055 * pow(max(c, vec3(0.0)), vec3(0.41666)) - 0.055,
               step(vec3(0.0031308), c));
  }

  void main() {
    vec3 n = normalize(vWorldNormal);

    // Ripple detail. Faded out with distance: at a kilometre it is below a
    // pixel and only produces shimmer, so it is not worth computing there.
    float detailFade = 1.0 - smoothstep(120.0, 900.0, vViewDist);
    if (detailFade > 0.001) {
      vec2 q = vWorldPos.xz * 0.55;
      float e = 0.35;
      float nx = valueNoise(q + vec2(e, 0.0)) - valueNoise(q - vec2(e, 0.0));
      float nz = valueNoise(q + vec2(0.0, e)) - valueNoise(q - vec2(0.0, e));
      n = normalize(n + vec3(-nx, 0.0, -nz) * 0.55 * detailFade);
    }

    vec3 viewDir = normalize(cameraPosition - vWorldPos);

    float fresnel = pow(1.0 - max(dot(n, viewDir), 0.0), 4.0);
    fresnel = clamp(fresnel, 0.02, 1.0);

    vec3 water = mix(uDeepColor, uShallowColor, clamp(n.y * 0.5 + 0.35, 0.0, 1.0));
    vec3 col = mix(water, uSkyColor, fresnel * 0.85);

    // Sun glitter.
    vec3 h = normalize(uSunDir + viewDir);
    float spec = pow(max(dot(n, h), 0.0), 220.0);
    col += vec3(1.0, 0.96, 0.86) * spec * 1.9;

    // Broad sheen so the sea is not flat matte away from the specular point.
    float sheen = pow(max(dot(n, h), 0.0), 12.0);
    col += vec3(0.9, 0.95, 1.0) * sheen * 0.06;

    // ---- whitecaps ---------------------------------------------------
    //
    // Foam forms where a wave is about to break, which is where it is both
    // TALL and STEEP — not merely tall. Keying on height alone paints a white
    // cap on every rounded swell crest and the sea turns into porridge; the
    // steepness term is what confines it to the crests that are actually
    // toppling, which at sea state 2 is a few per cent of them.
    //
    // Steepness comes from the SHADED normal 'n', not from the vertex-stage
    // gradient. The mesh quads are 29 m across and four of the six wave
    // components are shorter than that, so the vertex gradient is aliased —
    // and a smoothstep of a linearly interpolated value has a kink at every
    // triangle edge. Whitecaps would have come out as 29 m triangular facets
    // with visible banding. 'n' already carries the fine detail noise, so it
    // varies per pixel and the foam breaks up the way foam does.
    float crest = smoothstep(0.40, 0.95, vWaveHeight / max(uAmplitude, 0.001));
    float steep = smoothstep(0.006, 0.055, 1.0 - n.y);
    float foam = crest * steep * uFoam;

    // Streaks of foam blowing downwind off the crests, which is what stops it
    // reading as a set of identical white dots. Drifting along the WIND, not
    // due east — it was advecting on X alone regardless of where the wind was
    // coming from.
    vec2 drift = uWindDir * uTime * 0.9;
    float streak = valueNoise(vWorldPos.xz * 0.4 + drift);
    foam *= 0.4 + 0.85 * streak;

    // Distance fade. Individual whitecaps are not resolvable a mile out; what
    // you see there is a general lightening, which the fog already provides.
    foam *= 1.0 - smoothstep(400.0, 2600.0, vViewDist);

    col = mix(col, vec3(0.92, 0.95, 0.97), clamp(foam, 0.0, 0.85));

    float fog = pow(clamp(vViewDist / uFogFar, 0.0, 1.0), 1.6);
    col = mix(col, uFogColor, fog);

    // Fade the tile into the horizon disc over its outer edge. The tile only
    // reaches 3.5 km; the disc beyond it is a flat colour, so without this
    // there is a hard ring on the water exactly where the geometry stops.
    float edge = smoothstep(uTileFadeStart, uTileFadeEnd, vTileDist);
    col = mix(col, uFogColor, edge);

    // Every built-in material in the scene is tone-mapped by the renderer;
    // a custom shader has to opt in by hand or it ends up ~10% brighter than
    // the horizon disc sitting right next to it.
    #ifdef TONE_MAPPING
      col = toneMapping(col);
    #endif

    gl_FragColor = vec4(toSRGB(col), 1.0);
  }
`

export class Ocean {
  /**
   * @param {THREE.Vector3} sunDirection normalised, pointing at the sun
   * @param {THREE.Color} fogColor linear-space fog colour
   */
  constructor(sunDirection, fogColor, quality = {}) {
    this.group = new THREE.Group()
    this.group.name = 'Ocean'

    const TILE_SIZE_M = quality.oceanTileM ?? DEFAULT_TILE_SIZE_M
    const TILE_SEGMENTS = quality.oceanSegments ?? DEFAULT_TILE_SEGMENTS
    // The snap spacing has to be the quad size or the crests crawl under the
    // aeroplane — so it is derived here rather than being a module constant,
    // which is what made it wrong the first time this was parameterised.
    this.quadM = TILE_SIZE_M / TILE_SEGMENTS

    const geo = new THREE.PlaneGeometry(TILE_SIZE_M, TILE_SIZE_M, TILE_SEGMENTS, TILE_SEGMENTS)

    this.uniforms = {
      uTime: { value: 0 },
      uAmplitude: { value: 0.28 + ENV.SEA_STATE * 0.22 },
      uOffset: { value: new THREE.Vector2() },
      uSunDir: { value: sunDirection.clone().normalize() },
      uDeepColor: { value: new THREE.Color(0x081b2a) },
      uShallowColor: { value: new THREE.Color(0x0f3d52) },
      uSkyColor: { value: new THREE.Color(0x6f9cbd) },
      uFogColor: { value: fogColor.clone() },
      uFogFar: { value: ENV.VISIBILITY_M },
      uTileFadeStart: { value: TILE_SIZE_M * 0.30 },
      uTileFadeEnd: { value: TILE_SIZE_M * 0.48 },
      // Sea state 2 is a light breeze and scattered whitecaps — some, not many.
      // The exponent is what makes state 3 look meaningfully rougher than 2
      // rather than marginally so.
      uFoam: { value: Math.pow(Math.max(ENV.SEA_STATE - 0.6, 0) / 3.4, 1.0) * 2.4 },
      // Which way the foam streaks blow. "From 240" means toward 060.
      uWindDir: {
        value: new THREE.Vector2(
          -Math.sin((ENV.WIND_FROM_DEG * Math.PI) / 180),
          Math.cos((ENV.WIND_FROM_DEG * Math.PI) / 180),
        ),
      },
    }

    this.material = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader,
      fragmentShader,
      side: THREE.FrontSide,
    })

    this.tile = new THREE.Mesh(geo, this.material)
    this.tile.rotation.x = -Math.PI / 2
    this.tile.frustumCulled = false
    this.tile.renderOrder = -1
    this.group.add(this.tile)

    // Horizon filler. Flat, cheap, always beneath the tile.
    const farGeo = new THREE.CircleGeometry(ENV.VISIBILITY_M * 2.4, 96)
    // EXACTLY the colour the tile fades to. Any difference at all shows up as
    // a ring on the sea where one ends and the other begins.
    this.farMaterial = new THREE.MeshBasicMaterial({
      color: fogColor.clone(),
      fog: false,
      depthWrite: true,
    })
    this.far = new THREE.Mesh(farGeo, this.farMaterial)
    this.far.rotation.x = -Math.PI / 2
    // BELOW the deepest wave trough, not a round number above it.
    //
    // The disc writes depth and draws first. At -1.2 m it sat 0.5 m ABOVE the
    // deepest troughs the tile can make at sea state 2, so every trough that
    // went deeper than the disc was behind it from the camera's point of view,
    // failed the depth test, and was replaced by flat fog colour — pale holes
    // punched into the near sea, worst on final where you are looking down the
    // waves at a shallow angle and the deep troughs are the ones nearest you.
    this.far.position.y = -(this.uniforms.uAmplitude.value * WAVE_AMP_SUM + 0.5)
    this.far.frustumCulled = false
    this.far.renderOrder = -2
    this.group.add(this.far)
  }

  /**
   * @param {number} elapsed seconds
   * @param {THREE.Vector3} cameraPosition
   */
  update(elapsed, cameraPosition) {
    this.uniforms.uTime.value = elapsed

    const snapX = Math.round(cameraPosition.x / this.quadM) * this.quadM
    const snapZ = Math.round(cameraPosition.z / this.quadM) * this.quadM

    this.tile.position.set(snapX, 0, snapZ)
    this.uniforms.uOffset.value.set(snapX, snapZ)

    this.far.position.x = cameraPosition.x
    this.far.position.z = cameraPosition.z
  }

  dispose() {
    this.tile.geometry.dispose()
    this.material.dispose()
    this.far.geometry.dispose()
    this.farMaterial.dispose()
  }
}
